import React, { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { slicePixels, slicePick, sliceMesh } from '../lib/volume-slices';

/** Slice state of the viewer for the terrain group (TerrainMesh draws the slices inside it): null while not slicing. */
export const SliceContext = createContext(null);
export const useSliceContext = () => useContext(SliceContext);
const DRAPED_SEGMENTS = 128;

const clamp01 = (v) => Math.max(0, Math.min(1, v));

/** One slice as a textured quad in the scene (DoubleSide; cells outside the range / holes transparent). */
function SliceMesh({ item, sel, frame, extent, outsideOpacity, clippingPlanes, onPick, onDrag }) {
  const { camera, size } = useThree();
  const dragRef = useRef(null);
  const meshRef = useRef(null);
  const { slice } = item;

  const geometry = useMemo(() => {
    const m = sliceMesh(slice, frame.map, { maxSeg: frame.draped || frame.curved ? DRAPED_SEGMENTS : 1 });
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(m.positions, 3));
    g.setAttribute('uv', new THREE.BufferAttribute(m.uvs, 2));
    g.setIndex(m.indices);
    return g;
  }, [slice, frame]);
  useEffect(() => () => geometry.dispose(), [geometry]);

  const texture = useMemo(() => {
    const t = new THREE.DataTexture(new Uint8Array(slicePixels(slice, sel, outsideOpacity).buffer), slice.nu, slice.nv, THREE.RGBAFormat);
    t.magFilter = THREE.NearestFilter;
    t.minFilter = THREE.NearestFilter;
    t.needsUpdate = true;
    return t;
  }, [slice, sel, outsideOpacity]);
  useEffect(() => () => texture.dispose(), [texture]);

  useEffect(() => () => { if (dragRef.current) dragRef.current(); }, []);

  const startDrag = (e) => {
    const axis = item.axis;
    const dir = frame.axes[axis];
    if (!dir || !extent) return;
    const c = slice.corners;
    const mid = [0, 1, 2].map((k) => (c[0][k] + c[2][k]) / 2);
    // the mesh may sit in a scaled group (terrain vertical exaggeration): project its world positions
    const mesh = meshRef.current;
    const local0 = new THREE.Vector3(...frame.map(mid[0], mid[1], mid[2]));
    const local1 = local0.clone().add(new THREE.Vector3(...dir).multiplyScalar(extent[axis]));
    const p0 = mesh ? mesh.localToWorld(local0.clone()) : local0;
    const p1 = mesh ? mesh.localToWorld(local1) : local1;
    const toPx = (v) => { const p = v.clone().project(camera); return [((p.x + 1) / 2) * size.width, ((1 - p.y) / 2) * size.height]; };
    const a = toPx(p0), b = toPx(p1);
    const s = [b[0] - a[0], b[1] - a[1]];
    const ss = s[0] * s[0] + s[1] * s[1];
    if (ss < 16) return; // axis along the view direction: nothing to drag against
    const x0 = e.nativeEvent.clientX, y0 = e.nativeEvent.clientY, start = item.position;
    const move = (ev) => onDrag(axis, clamp01(start + ((ev.clientX - x0) * s[0] + (ev.clientY - y0) * s[1]) / ss));
    const stop = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
      dragRef.current = null;
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
    dragRef.current = stop;
  };

  const down = (e) => {
    e.stopPropagation();
    if (e.nativeEvent?.button) return;
    const i = e.uv ? slicePick(slice, e.uv.x, e.uv.y) : -1;
    if (i >= 0) {
      const lp = meshRef.current ? meshRef.current.worldToLocal(e.point.clone()) : e.point; // the marker sits in the same group
      onPick(sel.cells[i], [lp.x, lp.y, lp.z]);
    }
    if (item.draggable) {
      e.nativeEvent?.preventDefault?.(); // the orbit controls start on mousedown, which this suppresses
      startDrag(e);
    }
  };

  return (
    <mesh ref={meshRef} geometry={geometry} frustumCulled={false} onPointerDown={down}>
      <meshBasicMaterial map={texture} side={THREE.DoubleSide} transparent alphaTest={0.02} depthWrite={outsideOpacity === 0}
        clippingPlanes={clippingPlanes} />
    </mesh>
  );
}

/** Crosshair at the clicked point of a slice. */
function ProbeMarker({ point, size }) {
  const geometry = useMemo(() => {
    const g = new THREE.BufferGeometry();
    const h = size;
    g.setAttribute('position', new THREE.BufferAttribute(new Float32Array([-h, 0, 0, h, 0, 0, 0, -h, 0, 0, h, 0, 0, 0, -h, 0, 0, h]), 3));
    return g;
  }, [size]);
  useEffect(() => () => geometry.dispose(), [geometry]);
  return (
    <lineSegments geometry={geometry} position={point} renderOrder={10}>
      <lineBasicMaterial color="#ffffff" depthTest={false} />
    </lineSegments>
  );
}

/**
 * Static / dynamic slices of a 3D model (EarthImager 3D 3.8.6 and 3.8.7). `items` come from
 * lib/volume-slices slicesFor; `sel` is the model's voxel selection, `frame` its model-to-scene map.
 * Clicking a slice reports the block under the pointer; dynamic slices drag along their axis.
 */
export default function VolumeSlices({ items, sel, frame, extent, outsideOpacity = 0, clippingPlanes, probe, onPick, onDrag }) {
  const markerSize = useMemo(() => Math.max(0.5, Math.max(extent?.x || 0, extent?.y || 0, extent?.z || 0) * 0.02), [extent]);
  if (!items.length || !sel || !frame) return null;
  return (
    <group>
      {items.map((it) => (
        <SliceMesh key={it.id} item={it} sel={sel} frame={frame} extent={extent} outsideOpacity={outsideOpacity}
          clippingPlanes={clippingPlanes} onPick={onPick} onDrag={onDrag} />
      ))}
      {probe && <ProbeMarker point={probe.point} size={markerSize} />}
    </group>
  );
}
